/**
 * V-72. What an order's GPS breadcrumb actually establishes.
 *
 * Split out of OrderRouteMap.jsx so the reasoning can be tested without a
 * map: everything the panel claims about a trail is this one pure function
 * of the two server answers, and a Leaflet canvas has no business standing
 * between a test and the sentence "this billed stop was recorded by nothing".
 */

/** A gap this long is not jitter. Fixes arrive every 15 s
 *  (driver/hooks/useDriverTelemetry.js PING_INTERVAL_MS), so five minutes is
 *  twenty missed ones in a row — a dead phone, a lost signal, or a closed
 *  app, never a hiccup. */
export const SILENCE_MINUTES = 5;

const at = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
};

/**
 * Everything the panel says about the trail, as one pure function of the two
 * server answers — so what it claims can be tested without a map.
 *
 * `stops` are dwell_records, which carry no coordinates of their own: a
 * stop's position on the map is the first fix recorded INSIDE its window,
 * which is the only honest answer available and is exactly the thing whose
 * absence matters.
 */
export function analyseTrail(fixes, stops = [], order = {}) {
  const points = (fixes ?? [])
    .map((f) => ({ ...f, ms: at(f.recorded_at) }))
    .filter((f) => f.ms !== null && f.lat != null && f.lng != null);

  // The gaps, and the runs BETWEEN them. Both matter, and the second one
  // was found in a browser: the panel said "42 min of GPS silence" over a
  // map drawing one unbroken line straight through it, and a dispatcher
  // screenshotting that for a client is asserting a drive nothing recorded.
  // A trail is only continuous within a run.
  const gaps = [];
  const segments = points.length ? [[points[0]]] : [];
  for (let i = 1; i < points.length; i += 1) {
    const minutes = (points[i].ms - points[i - 1].ms) / 60_000;
    if (minutes >= SILENCE_MINUTES) {
      gaps.push({
        fromAt: points[i - 1].recorded_at,
        toAt: points[i].recorded_at,
        minutes: Math.round(minutes),
      });
      segments.push([points[i]]);
    } else {
      segments[segments.length - 1].push(points[i]);
    }
  }

  const coverage = (stops ?? []).map((stop) => {
    const from = at(stop.arrived_at);
    // An open stop has no departed_at. Its window runs to the last fix
    // there is, which is what "still standing there" means on a map.
    const to = stop.departed_at ? at(stop.departed_at) : Infinity;
    const inside = from === null ? [] : points.filter((p) => p.ms >= from && p.ms <= to);
    return {
      id: stop.id,
      locationType: stop.location_type,
      costEur: stop.total_cost_eur,
      fixCount: inside.length,
      at: inside[0] ? { lat: inside[0].lat, lng: inside[0].lng } : null,
    };
  });

  // A trail that stops well before the load did covers the drive but not
  // the end of it — and the end of it is where the unloading stop is.
  const deliveredMs = at(order.delivered_at);
  const lastMs = points.length ? points[points.length - 1].ms : null;
  const endsEarlyMinutes =
    deliveredMs !== null && lastMs !== null && (deliveredMs - lastMs) / 60_000 >= SILENCE_MINUTES
      ? Math.round((deliveredMs - lastMs) / 60_000)
      : null;

  return {
    points,
    segments,
    fixCount: points.length,
    firstAt: points.length ? points[0].recorded_at : null,
    lastAt: points.length ? points[points.length - 1].recorded_at : null,
    gaps,
    coverage,
    endsEarlyMinutes,
  };
}
